import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { enqueuedPost } from "@/lib/offlineQueue";
import { QrCode, ScanLine, Camera } from "lucide-react";
import { toast } from "sonner";
import CameraQrScanner from "@/components/CameraQrScanner";

export default function DriverScan() {
  const [missions, setMissions] = useState([]);
  const [missionId, setMissionId] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("boarding");
  const [cameraOpen, setCameraOpen] = useState(false);

  useEffect(() => {
    api.get("/missions").then(({ data }) => {
      setMissions(data);
      const active = data.find((m) => m.status === "active");
      if (active) setMissionId(active.id);
      else if (data.length) setMissionId(data[0].id);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!missionId) return toast.error("Aucune mission sélectionnée");
    if (!code) return toast.error("Saisir un code");
    try {
      const res = await enqueuedPost("/scan", { mission_id: missionId, qr_code: code.trim(), event_type: mode });
      if (res?.queued) toast.success(`Scan mis en file (hors-ligne) — sera synchronisé`);
      else toast.success(`${res.data.child.first_name} — ${mode === "boarding" ? "Monté" : "Descendu"} ✓`);
      setCode("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const doCameraScan = async (text) => {
    if (!missionId) { toast.error("Aucune mission sélectionnée"); return; }
    setCameraOpen(false);
    try {
      const res = await enqueuedPost("/scan", { mission_id: missionId, qr_code: text.trim(), event_type: mode });
      if (res?.queued) toast.success(`Scan mis en file (hors-ligne) — sera synchronisé`);
      else toast.success(`${res.data.child.first_name} — ${mode === "boarding" ? "Monté" : "Descendu"} ✓`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="driver-scan-page">
      <div className="mb-5">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Scanner rapide</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">QR Scan</h1>
      </div>

      <form onSubmit={submit} className="border border-zinc-900 p-4 space-y-3 bg-white">
        <div>
          <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Mission</label>
          <select data-testid="quick-scan-mission-select" value={missionId} onChange={(e) => setMissionId(e.target.value)}
            className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
            {missions.map((m) => <option key={m.id} value={m.id}>{m.route?.name} ({m.date}) — {m.status}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-0 border border-zinc-900">
          <button type="button" data-testid="quick-scan-mode-boarding" onClick={() => setMode("boarding")} className={`py-3 text-xs font-bold uppercase tracking-widest ${mode === "boarding" ? "bg-[#FFC60A]" : "bg-white"}`}>Montée</button>
          <button type="button" data-testid="quick-scan-mode-dropoff" onClick={() => setMode("dropoff")} className={`py-3 text-xs font-bold uppercase tracking-widest border-l border-zinc-900 ${mode === "dropoff" ? "bg-[#FFC60A]" : "bg-white"}`}>Descente</button>
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Code QR</label>
          <div className="mt-1 flex gap-2">
            <input data-testid="quick-scan-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="SHT-XXXXXXXXXX"
              className="flex-1 border border-zinc-900 px-3 py-3 font-mono tracking-[0.15em] uppercase focus:ring-2 focus:ring-zinc-900 focus:outline-none" />
            <button data-testid="quick-scan-submit" type="submit" className="bg-zinc-900 text-white px-4 font-bold border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 inline-flex items-center gap-2">
              <ScanLine size={16} /> OK
            </button>
          </div>
        </div>
        <div className="text-[11px] font-mono text-zinc-500 flex items-center gap-1">
          <QrCode size={12} /> Saisissez ou scannez le code présent sur le badge de l'enfant.
        </div>
        <button type="button" onClick={() => setCameraOpen(true)} data-testid="quick-scan-camera-btn"
          className="mt-2 w-full bg-[#FFC60A] border border-zinc-900 py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-[#ffd43a]">
          <Camera size={18} /> Scanner avec la caméra
        </button>
      </form>

      <CameraQrScanner open={cameraOpen} onClose={() => setCameraOpen(false)} onScan={doCameraScan} />
    </div>
  );
}
